import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Calendar } from "@/shared/components/ui/calendar";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { Spinner } from "@/shared/components/ui/spinner";
import type { StockResponseDTO } from "../types/stock.types";
import { StockStatus } from "../types/stock.types";
import { stockAvailabilityService } from "../services/stock-availability.service";
import { memo, useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { getApiErrorMessage } from "@/shared/lib/api-error";
import { dateToLocalDateString } from "@/shared/lib/date-format";

interface StockCalendarDialogProps {
  open: boolean;
  stock: StockResponseDTO | null;
  onClose: () => void;
}

export const StockCalendarDialog = memo(function StockCalendarDialog({
  open,
  stock,
  onClose,
}: StockCalendarDialogProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availability, setAvailability] = useState<Record<string, StockStatus>>(
    {},
  );
  const [loading, setLoading] = useState(false);
  // Cache de disponibilidades por mês: { "2026-02": { "2026-02-01": "AVAILABLE", ... } }
  const [availabilityCache, setAvailabilityCache] = useState<
    Record<string, Record<string, StockStatus>>
  >({});

  const loadAvailability = useCallback(
    async (month: Date) => {
      if (!stock) return;

      // Gerar chave do cache para o mês
      const cacheKey = `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, "0")}`;

      // Verificar se já existe no cache
      if (availabilityCache[cacheKey]) {
        setAvailability(availabilityCache[cacheKey]);
        return;
      }

      setLoading(true);
      try {
        const today = new Date();
        // Se o mês/ano selecionado é o mês atual, inicia do dia de hoje
        // Caso contrário, inicia do primeiro dia do mês
        const isCurrentMonth =
          month.getFullYear() === today.getFullYear() &&
          month.getMonth() === today.getMonth();

        const startDate = isCurrentMonth
          ? today
          : new Date(month.getFullYear(), month.getMonth(), 1);

        // Último dia do mês
        const endDate = new Date(month.getFullYear(), month.getMonth() + 1, 0);

        const response = await stockAvailabilityService.getAvailability({
          stockIds: [stock.id],
          // Data local do usuário — evita off-by-one em fusos negativos
          // quando o Date construído fica em horário próximo à meia-noite UTC.
          startDate: dateToLocalDateString(startDate),
          endDate: dateToLocalDateString(endDate),
        });

        if (response.length > 0) {
          const monthData = response[0].statusByDate;
          setAvailability(monthData);
          // Salvar no cache
          setAvailabilityCache((prev) => ({
            ...prev,
            [cacheKey]: monthData,
          }));
        } else {
          setAvailability({});
          setAvailabilityCache((prev) => ({
            ...prev,
            [cacheKey]: {},
          }));
        }
      } catch (error: unknown) {
        toast.error(
          getApiErrorMessage(error, "Erro ao carregar disponibilidade"),
        );
        setAvailability({});
      } finally {
        setLoading(false);
      }
    },
    [stock, availabilityCache],
  );

  useEffect(() => {
    if (open && stock) {
      loadAvailability(currentMonth);
    }
  }, [open, stock, currentMonth, loadAvailability]);

  const handleMonthChange = useCallback(
    (newMonth: Date) => {
      setCurrentMonth(newMonth);
      loadAvailability(newMonth);
    },
    [loadAvailability],
  );

  const getDateStatus = useCallback(
    (date: Date): StockStatus | undefined => {
      const dateStr = dateToLocalDateString(date);
      return availability[dateStr];
    },
    [availability],
  );

  const modifiers = {
    available: (date: Date) => getDateStatus(date) === StockStatus.AVAILABLE,
    rented: (date: Date) => getDateStatus(date) === StockStatus.RENTED,
    onStreet: (date: Date) => getDateStatus(date) === StockStatus.ON_STREET,
    maintenance: (date: Date) =>
      getDateStatus(date) === StockStatus.MAINTENANCE,
  };

  const modifiersStyles = {
    available: {
      backgroundColor: "#16a34a",
      color: "white",
    },
    rented: {
      backgroundColor: "#ef4444",
      color: "white",
    },
    onStreet: {
      backgroundColor: "#a855f7",
      color: "white",
    },
    maintenance: {
      backgroundColor: "#f59e0b",
      color: "white",
    },
  };

  if (!stock) return null;

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Calendário de Disponibilidade</DialogTitle>
          <DialogDescription>
            {stock.productName} • Código: {stock.barcode} • Tamanho:{" "}
            {stock.size}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const today = new Date();
                setCurrentMonth(today);
                loadAvailability(today);
              }}
              disabled={loading}
            >
              Hoje
            </Button>
          </div>
          <div className="flex items-center justify-center">
            {loading ? (
              <div className="py-32 flex items-center justify-center">
                <Spinner className="size-8" />
              </div>
            ) : (
              <Calendar
                mode="single"
                month={currentMonth}
                onMonthChange={handleMonthChange}
                modifiers={modifiers}
                modifiersStyles={modifiersStyles}
                captionLayout="dropdown"
                className="rounded-md border"
              />
            )}
          </div>

          {/* Legenda */}
          <div className="space-y-3">
            <div className="flex flex-wrap gap-4 justify-center text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-green-600" />
                <span>Disponível</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-red-500" />
                <span>Alugado</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-purple-500" />
                <span>Na Rua</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-amber-500" />
                <span>Manutenção</span>
              </div>
            </div>
          </div>

          {/* Lista de datas com status */}
          {Object.keys(availability).length > 0 && (
            <div className="border-t pt-4 mt-4">
              <h4 className="font-semibold mb-3 text-sm">
                Status no Período Selecionado
              </h4>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {Object.entries(availability)
                  .sort(([dateA], [dateB]) => dateA.localeCompare(dateB))
                  .filter(([, status]) => status !== StockStatus.AVAILABLE)
                  .map(([date, status]) => (
                    <div
                      key={date}
                      className="flex items-center justify-between text-sm p-2 bg-muted/50 rounded"
                    >
                      <span className="text-muted-foreground">
                        {new Date(date + "T00:00:00").toLocaleDateString(
                          "pt-BR",
                        )}
                      </span>
                      <Badge
                        variant={
                          status === StockStatus.RENTED
                            ? "destructive"
                            : status === StockStatus.MAINTENANCE
                              ? "secondary"
                              : "default"
                        }
                      >
                        {status === StockStatus.RENTED
                          ? "Alugado"
                          : status === StockStatus.ON_STREET
                            ? "Na Rua"
                            : status === StockStatus.MAINTENANCE
                              ? "Manutenção"
                              : "Disponível"}
                      </Badge>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
});
